import { Webhook } from "../schema/webhook";

/**
 * What the Webhooks table says about a webhook's deliveries (PREC-4248). Mirrors the API's
 * `nextWebhookHealth` (`apps/api/src/webhook/delivery/webhook-delivery.policy.ts`): the streak
 * counts given-up deliveries only, "Failing" starts at the third one — the same moment the
 * creator and the admins are notified — and a delivered lead clears it.
 */
export const WEBHOOK_FAILING_AT = 3;

export type WebhookHealthState =
	| "paused"
	| "failing"
	| "lastFailed"
	| "retrying"
	| "delivering"
	| "idle";

export type WebhookHealthTone = "error" | "warning" | "ok" | "idle";

export interface WebhookHealthDescription {
	state: WebhookHealthState;
	tone: WebhookHealthTone;
	/** The receiver's last answer, e.g. "404 Not Found". */
	error: string | null;
	/** The day the current streak of failures began, formatted. */
	since: string | null;
	/** The day of the last delivery attempt, formatted. */
	lastDelivery: string | null;
	/**
	 * The day the receiver last accepted a lead, formatted (PREC-4253). Falls back to the last
	 * attempt only while delivering, since that attempt was then a delivery.
	 */
	lastDelivered: string | null;
	/** The day of the last failed attempt, formatted; kept after a success clears the error. */
	lastFailed: string | null;
	canResume: boolean;
}

type WebhookHealthFields = Pick<
	Webhook,
	| "lastDeliveryAt"
	| "lastDeliveredAt"
	| "lastFailureAt"
	| "lastError"
	| "failingSince"
	| "consecutiveFailures"
	| "pausedAt"
>;

export const formatWebhookDay = (value: string | null | undefined): string | null =>
	value
		? new Date(value).toLocaleDateString("en-GB", {
				day: "numeric",
				month: "short",
				year: "numeric",
				timeZone: "UTC",
			})
		: null;

export function describeWebhookHealth(webhook: WebhookHealthFields): WebhookHealthDescription {
	const failures = webhook.consecutiveFailures ?? 0;
	const base = {
		error: webhook.lastError ?? null,
		since: formatWebhookDay(webhook.failingSince),
		lastDelivery: formatWebhookDay(webhook.lastDeliveryAt),
		lastDelivered: formatWebhookDay(webhook.lastDeliveredAt),
		lastFailed: formatWebhookDay(webhook.lastFailureAt),
		canResume: false,
	};

	if (webhook.pausedAt) {
		return {
			...base,
			state: "paused",
			tone: "error",
			since: formatWebhookDay(webhook.failingSince ?? webhook.pausedAt),
			canResume: true,
		};
	}

	if (failures >= WEBHOOK_FAILING_AT) {
		return { ...base, state: "failing", tone: "error" };
	}

	if (failures > 0) {
		return { ...base, state: "lastFailed", tone: "warning" };
	}

	// A failed attempt that is being retried leaves its error without counting towards the streak.
	if (webhook.lastError) {
		return { ...base, state: "retrying", tone: "warning" };
	}

	if (webhook.lastDeliveryAt) {
		return {
			...base,
			state: "delivering",
			tone: "ok",
			lastDelivered: base.lastDelivered ?? base.lastDelivery,
		};
	}

	return { ...base, state: "idle", tone: "idle" };
}
